// * External
import React from "react";
import classNames from "classnames";
import { Button } from "@toddle-edu/ds-web";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftPaneOutlined,
  ArrowRightPaneOutlined,
} from "@toddle-edu/ds-icons";

// * Internal
import { WORKSHEET_QUESTION_CREATION } from "Constants/stringConstants";

// * Relative
import classes from "./CreateWorksheetSidebarWrapper.scss";
import { WORKSHEET_ELEMENT_IDS } from "../../../modules/WorksheetConstants";

/**
 * Sidebar toggle button component for collapsing and expanding the sidebar
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isCollapsed - Whether the sidebar is collapsed
 * @param {Function} props.toggleSidebar - Function to toggle sidebar state
 * @param {string} props.className - Additional class names for the button
 * @returns {JSX.Element} Sidebar toggle button
 */
const SidebarToggleButton = ({ isCollapsed, onClick, className }) => {
  const icon = isCollapsed ? (
    <ArrowRightPaneOutlined />
  ) : (
    <ArrowLeftPaneOutlined className={classes.iconSecondary} />
  );

  return (
    <Button
      dsVersion="2.0"
      onClick={onClick}
      icon={icon}
      className={className}
      id={WORKSHEET_ELEMENT_IDS.WORKSHEET_SIDEBAR_COLLAPSE_ID}
      aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      variant="neutral"
      type="plain"
      size="medium"
      testId={
        isCollapsed ? "worksheet-sidebar-expand" : "worksheet-sidebar-collapse"
      }
    />
  );
};

/** * CreateWorksheetSidebarWrapper component renders a sidebar for creating worksheets.
 * It includes a toggle button, a header, and a main area for sections and questions.
 * @param {Object} props - The component props.
 * @param {boolean} props.showSidebar - Indicates if the sidebar is visible
 * @param {function} props.toggleSidebar - Function to toggle the sidebar visibility
 * @param {ReactNode} props.children - The content to be rendered inside the sidebar
 * @param {ReactNode} props.addQuestionButton - The button to add a new question
 */
const CreateWorksheetSidebarWrapper = ({
  children,
  showSidebar,
  toggleSidebar,
  addQuestionButton,
  updateActiveQuestion,
  isWorksheetDetailsActive,
}) => {
  const { t } = useTranslation();

  const sidebarContainerClasses = classNames(
    classes.worksheetSidebarContainer,
    {
      [classes.worksheetSidebarVisible]: showSidebar,
      [classes.worksheetSidebarOverlay]: true,
    }
  );

  const outlineLabelClasses = classNames("text-body-s", classes.outlineLabel);

  return (
    <>
      {/* SIDEBAR TOGGLE BUTTON WHEN SIDEBAR IS CLOSED */}
      {!showSidebar && (
        <SidebarToggleButton
          isCollapsed={true}
          onClick={toggleSidebar}
          className={classes.expandButton}
        />
      )}

      <div className={sidebarContainerClasses}>
        {/* SIDEBAR HEADER */}
        <div className={classes.worksheetSidebarHeader}>
          <span className={outlineLabelClasses}>
            {t("common:outline_label")}
          </span>
          {/* SIDEBAR TOGGLE BUTTON WHEN SIDEBAR IS OPEN */}
          {showSidebar && (
            <SidebarToggleButton
              isCollapsed={false}
              onClick={toggleSidebar}
              className={classes.collapseButton}
            />
          )}
        </div>

        {/* WORKSHEET DETAILS BUTTON */}
        <div className={classes.worksheetSidebarDetailsButtonContainer}>
          <Button
            type="plain"
            dsVersion="2.0"
            variant="neutral"
            data-test-id="worksheet-sidebar-worksheet-details-button"
            onClick={() => updateActiveQuestion(WORKSHEET_QUESTION_CREATION)}
            className={classNames(classes.worksheetSidebarDetailsButton, {
              [classes.worksheetSidebarDetailsButtonActive]: isWorksheetDetailsActive,
            })}
          >
            {t("common:details")}
          </Button>
        </div>

        {/* LIST OF SECTIONS AND QUESTIONS */}
        <div className={classes.worksheetSidebarMainArea}>{children}</div>

        {/* ADD QUESTION BUTTON */}
        {addQuestionButton}
      </div>
    </>
  );
};

export default CreateWorksheetSidebarWrapper;
